import type { QueryKey, UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import type {
  OptionCategoryFormValues,
  OptionCategoryRowResponse,
  OptionCategorySelectOption,
  OptionCategoryType,
} from "../api/optionCategories";
import {
  OPTION_CATEGORY_HAS_DESCRIPTION,
  createOptionCategoryMutationOptions,
  deleteOptionCategoryMutationOptions,
  optionCategoryHistoryQueryOptions,
  optionCategoryListQueryOptions,
  optionCategorySelectOptionsQueryOptions,
  updateOptionCategoryMutationOptions,
} from "../api/optionCategories";
import type { Permission } from "../shared/permissions";
import { optionCategoryToSlug } from "../shared/usePrivacyNavigate";
import type { OptionCategoryScopeName } from "./optionCategoryTypeConfig";
import type { OptionCategoryHistoryEntry } from "./types";
import { usageLinkTo } from "./usageLinkTo";

type OptionCategoryAction = "read" | "create" | "update" | "delete";

type CategoryQueryOptions<Data> = (categoryType: OptionCategoryType) => UseQueryOptions<Data, Error, Data, QueryKey>;

type CategoryMutationOptions<Data, Variables> = () => UseMutationOptions<Data, Error, Variables>;

type CategoryVariables = { categoryType: OptionCategoryType; values: OptionCategoryFormValues };

export type OptionCategoryScope = {
  name: OptionCategoryScopeName;
  routePath: (categoryType: OptionCategoryType) => string;
  listQueryOptions: CategoryQueryOptions<{ options: Array<OptionCategoryRowResponse> }>;
  historyQueryOptions: CategoryQueryOptions<{ history: Array<OptionCategoryHistoryEntry> }>;
  selectOptionsQueryOptions: CategoryQueryOptions<{ options: Array<OptionCategorySelectOption> }>;
  createMutationOptions: CategoryMutationOptions<{ id: string; value: string }, CategoryVariables>;
  updateMutationOptions: CategoryMutationOptions<unknown, CategoryVariables & { id: string }>;
  deleteMutationOptions: CategoryMutationOptions<unknown, { categoryType: OptionCategoryType; id: string }>;
  permission: (categoryType: OptionCategoryType, action: OptionCategoryAction) => Permission;
  hasDescription: (categoryType: OptionCategoryType) => boolean;
  usageLinkTo: typeof usageLinkTo | undefined;
};

const ORGANISATION_PERMISSIONS: Record<OptionCategoryType, Record<OptionCategoryAction, Permission>> = {
  categoriesOfProcessings: {
    read: "nova-forms-api:category-options:categories-of-processings:gridview",
    create: "nova-forms-api:category-options:categories-of-processings:create",
    update: "nova-forms-api:category-options:categories-of-processings:update",
    delete: "nova-forms-api:category-options:categories-of-processings:delete",
  },
  categoriesOfPersonalData: {
    read: "nova-forms-api:category-options:categories-of-personal-data:gridview",
    create: "nova-forms-api:category-options:categories-of-personal-data:create",
    update: "nova-forms-api:category-options:categories-of-personal-data:update",
    delete: "nova-forms-api:category-options:categories-of-personal-data:delete",
  },
  categoriesOfDataSubjects: {
    read: "nova-forms-api:category-options:categories-of-data-subjects:gridview",
    create: "nova-forms-api:category-options:categories-of-data-subjects:create",
    update: "nova-forms-api:category-options:categories-of-data-subjects:update",
    delete: "nova-forms-api:category-options:categories-of-data-subjects:delete",
  },
  projects: {
    read: "nova-forms-api:category-options:projects:gridview",
    create: "nova-forms-api:category-options:projects:create",
    update: "nova-forms-api:category-options:projects:update",
    delete: "nova-forms-api:category-options:projects:delete",
  },
  technicalSecurityMeasurements: {
    read: "nova-forms-api:category-options:technical-security-measurements:gridview",
    create: "nova-forms-api:category-options:technical-security-measurements:create",
    update: "nova-forms-api:category-options:technical-security-measurements:update",
    delete: "nova-forms-api:category-options:technical-security-measurements:delete",
  },
};

export const organisationOptionCategoryScope: OptionCategoryScope = {
  name: "organisation",
  routePath: categoryType => `option-categories/${optionCategoryToSlug[categoryType]}`,
  listQueryOptions: optionCategoryListQueryOptions,
  historyQueryOptions: optionCategoryHistoryQueryOptions,
  selectOptionsQueryOptions: optionCategorySelectOptionsQueryOptions,
  createMutationOptions: createOptionCategoryMutationOptions,
  updateMutationOptions: updateOptionCategoryMutationOptions,
  deleteMutationOptions: deleteOptionCategoryMutationOptions,
  permission: (categoryType, action) => ORGANISATION_PERMISSIONS[categoryType][action],
  hasDescription: categoryType => OPTION_CATEGORY_HAS_DESCRIPTION[categoryType],
  usageLinkTo,
};
